import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useData } from "../context/DataContext";
import { useNotification } from "../context/NotificationContext";
import "./SetupPage.css";

const weekdayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export const SetupPage = () => {
  const { user, desks, loading, updateUserPreferences } = useData();
  const { addNotification } = useNotification();

  // Local form state
  const [newName, setNewName] = useState("");
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
  const [defaultDeskId, setDefaultDeskId] = useState<string | null>(null);
  const [defaultWeekdays, setDefaultWeekdays] = useState<number[]>([]);
  const [autoReserveEnabled, setAutoReserveEnabled] = useState(false);

  // Memoize the list of unique rooms to avoid recalculating on every render
  const uniqueRooms = useMemo(() => {
    const roomSet = new Set(desks.map(d => d.room));
    return Array.from(roomSet).sort();
  }, [desks]);
  
  // Memoize the list of desks filtered by the selected room
  const desksInSelectedRoom = useMemo(() => {
    if (!selectedRoom) return [];
    return desks
      .filter(d => d.room === selectedRoom)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [desks, selectedRoom]);


  useEffect(() => {
    if (user) {
      setNewName(user.name);
      setDefaultDeskId(user.defaultDeskId);
      setDefaultWeekdays(user.defaultWeekdays);
      setAutoReserveEnabled(user.autoReserveEnabled);
      
      if (user.defaultDeskId) {
        const desk = desks.find(d => d.id === user.defaultDeskId);
        if (desk) {
          setSelectedRoom(desk.room);
        }
      }
    }
  }, [user, desks]);


  const handleRoomChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRoomId = e.target.value;
    setSelectedRoom(newRoomId);
    const currentDesk = desks.find(d => d.id === defaultDeskId);
    if (currentDesk && currentDesk.room !== newRoomId) {
        setDefaultDeskId(null);
    }
  }

  const isPrefsDirty = useMemo(() => {
    if (!user) return false;
    const weekdaysChanged = JSON.stringify(user.defaultWeekdays.sort()) !== JSON.stringify(defaultWeekdays.sort());
    return (
      user.defaultDeskId !== defaultDeskId ||
      weekdaysChanged ||
      user.autoReserveEnabled !== autoReserveEnabled
    );
  }, [user, defaultDeskId, defaultWeekdays, autoReserveEnabled]);

  const handleWeekdayToggle = (dayIndex: number) => {
    setDefaultWeekdays(prev =>
      prev.includes(dayIndex)
        ? prev.filter(d => d !== dayIndex)
        : [...prev, dayIndex]
    );
  };

  const handleSavePrefs = () => {
    if (!isPrefsDirty) return;
    updateUserPreferences({
        defaultDeskId,
        defaultWeekdays,
        autoReserveEnabled
    });
  };

  const handleNameChange = async () => {
    if (!user || !newName || newName.trim() === user.name) return;
    
    const updatedUser = await updateUserPreferences({ name: newName.trim() });

    if (updatedUser) {
        localStorage.setItem('deskshare_nickname', updatedUser.name);
        addNotification("Name changed successfully!", "success");
    }
  };

  if (loading) return <h1>Loading preferences...</h1>;
  if (!user) return <h1>Please log in to see your preferences.</h1>;

  return (
    <div>
      <h1>Preferences Setup</h1>
      <div className="preferences-card">
        <h2>User Details</h2>
        <div className="pref-item">
            <label htmlFor="user-name">Your Name</label>
            <div className="name-change-group">
                <input 
                    type="text" 
                    id="user-name" 
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                />
                <button 
                    onClick={handleNameChange}
                    disabled={!newName || (user && newName.trim() === user.name)}
                    className="change-name-button"
                >
                    Change Name
                </button>
            </div>
        </div>
        <p><strong>Email:</strong> {user.email}</p>
      </div>

      <div className="preferences-card">
        <h2>Automatic Reservation Preferences</h2>
        
        <div className="pref-item">
          <label htmlFor="auto-reserve-toggle">Enable Auto-Reservation</label>
          <div className="toggle-switch">
            <input
              type="checkbox"
              id="auto-reserve-toggle"
              checked={autoReserveEnabled}
              onChange={(e) => setAutoReserveEnabled(e.target.checked)}
            />
            <label htmlFor="auto-reserve-toggle" className="slider"></label>
          </div>
        </div>

        <div className={`pref-details ${!autoReserveEnabled ? 'disabled' : ''}`}>
          
          {/* --- NEW WRAPPER DIV --- */}
          <div className="pref-group">
            <div className="pref-item">
              <label htmlFor="default-room">Default Room</label>
              <select
                  id="default-room"
                  value={selectedRoom || ''}
                  onChange={handleRoomChange}
                  disabled={!autoReserveEnabled}
              >
                  <option value="">-- Select a Room --</option>
                  {uniqueRooms.map(room => (
                      <option key={room} value={room}>
                          Room {room}
                      </option>
                  ))}
              </select>
            </div>

            <div className="pref-item">
              <label htmlFor="default-desk">Default Desk</label>
              <select
                id="default-desk"
                value={defaultDeskId || ''}
                onChange={(e) => setDefaultDeskId(e.target.value || null)}
                disabled={!autoReserveEnabled || !selectedRoom}
              >
                <option value="">-- Select a Desk --</option>
                {desksInSelectedRoom.map(desk => (
                  <option key={desk.id} value={desk.id}>
                    {desk.name} ({desk.location})
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* --- END OF WRAPPER DIV --- */}

          <div className="pref-item">
            <label>Default Weekdays</label>
            <div className="weekday-selector">
              {weekdayLabels.map((label, index) => (
                <button
                  key={index}
                  className={`weekday-btn ${defaultWeekdays.includes(index) ? 'active' : ''}`}
                  onClick={() => handleWeekdayToggle(index)}
                  disabled={!autoReserveEnabled}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="save-button-container">
            <button
              onClick={handleSavePrefs}
              disabled={!isPrefsDirty || loading}
              className="save-button"
            >
              Save Preferences
            </button>
        </div>
      </div>
    </div>
  );
};